import { useMutation, useQueryClient } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

import { RECEIPT_DETAIL_QUERY_KEY } from "./useReceipt";
import { RECEIPTS_POLL_KEY, RECEIPTS_QUERY_KEY } from "./useReceiptsList";

import type { ReceiptDetail } from "./useReceipt";

export interface DisplacedReceipt {
  uuid: string;
  merchant: string | null;
  transaction_date: string | null;
  amount: string | null;
}

export interface LinkMatchError extends Error {
  status?: number;
  errorCode?: string;
  displacedReceipt?: DisplacedReceipt;
}

export interface LinkMatchVariables {
  receiptId: string;
  transactionId: string;
  allowSwap?: boolean;
}

export interface UseLinkMatchOptions {
  onSuccess?: (updated: ReceiptDetail) => void;
  onError?: (error: LinkMatchError, variables: LinkMatchVariables) => void;
}

const extractStatus = (err: unknown): number | undefined => {
  if (typeof err !== "object" || err === null) return undefined;
  const { response } = err as { response?: { status?: unknown } };
  if (!response || typeof response.status !== "number") return undefined;
  return response.status;
};

const extractConflictBody = (
  err: unknown
): { errorCode?: string; displacedReceipt?: DisplacedReceipt } => {
  if (typeof err !== "object" || err === null) return {};
  const { response } = err as {
    response?: {
      data?: { error_code?: unknown; displaced_receipt?: unknown };
    };
  };
  const body = response?.data;
  if (!body || typeof body !== "object") return {};
  return {
    errorCode:
      typeof body.error_code === "string" ? body.error_code : undefined,
    displacedReceipt:
      typeof body.displaced_receipt === "object" &&
      body.displaced_receipt !== null
        ? (body.displaced_receipt as DisplacedReceipt)
        : undefined,
  };
};

const extractMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "Link match failed";
};

const toLinkMatchError = (err: unknown): LinkMatchError => {
  const wrapped = new Error(extractMessage(err)) as LinkMatchError;
  wrapped.status = extractStatus(err);
  const { errorCode, displacedReceipt } = extractConflictBody(err);
  wrapped.errorCode = errorCode;
  wrapped.displacedReceipt = displacedReceipt;
  return wrapped;
};

export const useLinkMatch = (options: UseLinkMatchOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation<ReceiptDetail, LinkMatchError, LinkMatchVariables>({
    mutationFn: async ({ receiptId, transactionId, allowSwap }) => {
      try {
        // rawResponse: true so a thrown error keeps response.status and the
        // 409 body the swap flow discriminates on.
        const response = await ApiHttp.fetch<ReceiptDetail>(
          `receipts/${receiptId}/link/`,
          { method: "POST", rawResponse: true },
          { transaction_id: transactionId, allow_swap: allowSwap ?? false }
        );
        return (response as unknown as { data: ReceiptDetail }).data;
      } catch (err) {
        throw toLinkMatchError(err);
      }
    },
    onSuccess: (updated) => {
      // Refresh every bucket list (counts ride on meta), sparing the
      // upload-polling query which tracks its own visibility.
      queryClient.invalidateQueries({
        predicate: (q) =>
          q.queryKey[0] === RECEIPTS_QUERY_KEY &&
          q.queryKey[1] !== RECEIPTS_POLL_KEY,
      });
      queryClient.invalidateQueries({
        queryKey: [RECEIPT_DETAIL_QUERY_KEY, updated.uuid],
      });
      options.onSuccess?.(updated);
    },
    onError: (err, variables) => {
      options.onError?.(err, variables);
    },
  });
};
